/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Field } from '@src/app/shared/field/Field';
import { Person } from '@src/app/model/person';
import { RoleService } from '@src/app/pages/role-page/role.service';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { required } from '@src/app/shared/validators';
import { TeamUserService } from '@src/app/modules/user/team-user-page/team-user.service';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { TeamUser } from '@src/app/model/team-user';
import { Role } from '@src/app/model/role';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { TeamUserPageNavRoute } from '@src/app/routes/team-user-page-nav';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';

@Component({
    selector: 'app-team-user-page',
    templateUrl: './team-user-page.component.html',
    styleUrls: ['./team-user-page.component.scss'],
    imports: [FormPageComponent]
})
export class TeamUserPageComponent extends AbstractPageComponent {
    dataSvc = inject(TeamUserService);
    private roleSvc = inject(RoleService);


    roles: Role[] = [];

    auditGrid: GridField = new GridField({
        field: {
            value: 'auditEntries', cellOpts: { heading: 'Actions Taken' }, formColumn: 1, formRow: 2,
            visible: Field.formOnly, readonly: true, sendServer: false
        },
        rowFactory: () => [
            FormDateTimeComponent.make('Date', 'date', { readonly: true }),
            FormTextComponent.make('Object', 'object', { readonly: true }),
            FormTextComponent.make('Object Id', 'objectId', { readonly: true }),
            FormTextComponent.make('Model', 'model', { readonly: true }),
            FormTextComponent.make('Model Id', 'rowId', { readonly: true }),
            FormTextComponent.make('Revision', 'revision', { readonly: true }),
            FormTextComponent.make('Action', 'action', { readonly: true }),
        ]
    });

    rolePicklistField = FormPicklistComponent.make('Role', 'roleId', 'role',
        { items: [] },
        { cellOpts: { width: '20%' }, validators: [required], visible: Field.formOnly, formColumn: 4 }
    )


    config = new FormConfig({
        navRoute: TeamUserPageNavRoute,
        docPath: 'omc-community/managers/team-users',
        title: $localize`Team Users`,
        help: $localize`The users authorized for the current team.`,
        allowNew: false,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FormNavLinkComponent.makeDetail('Profile', 'fullName', 'personId', PersonPageNavRoute).override({
                    calculateValue: (o: Person) => o?.fullName
                }),
                FormNavLinkComponent.makeDetail('Unit', 'unitNameLink', 'unitId', UnitPageNavRoute).override({
                    calculateValue: (o) => o['unitName']
                }),
                FormTextComponent.make('User Name', 'userName', { readonly: true, sendServer: false, formColumn: 4, condensed:true }),                
                FormTextComponent.make('Email Address', 'email', { readonly: true, sendServer: false }),
                FormPhoneComponent.make('Phone', 'phone', { readonly: true, sendServer: false }),
                FormTextAreaComponent.make('Address', 'address', {formColumn: 2}),
                FormTextComponent.make('Role', 'roleName', {
                    readonly: true, sendServer: false, allowFiltering: false, visible: Field.noForm
                }),
                FormTextComponent.make('Unit', 'unitName',{visible: Field.noShow}),
                FormPicklistComponent.make('Current', 'current', null, {items: [{id:0, name: 'No'}, {id:1, name: 'Yes'}]}, {
                    visible: Field.noShow, sendServer: false,
                }),
                FormPicklistComponent.make('Member', 'isOwner', null, {items: [{id:0, name: 'No'}, {id:1, name: 'Yes'}]}, {
                    visible: Field.noShow, sendServer: false,
                }),                
                /*FormButtonComponent.make('Current', 'currentIcon', {
                    type: 'icon', sendServer: false, cellOpts:{heading: ' '}, visible: Field.noForm,
                    toolTip: 'Is role currently active?',
                    tableIconStyler: () => 'color:red',
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    calculateValue: (o: PersonUnitRole) => PersonUnitRole.isCurrent(o) ? '' : 'close'
                }), */
                FormDateComponent.make('From', 'startDate', { cellOpts: { width: '2%' }, formColumn: 3 }),
                FormDateComponent.make('Until', 'endDate', { cellOpts: { width: '2%' }, formColumn: 3 }),
                this.rolePicklistField,
                //this.peopleGrid,
                FormDateTimeComponent.make('Last Login', 'lastLoginAt', { readonly: true, formColumn: 5 }),
                FormNumberComponent.make('Login Count', 'logins', {}, { readonly: true, formColumn: 5 }),
                this.auditGrid,
            ],
            formLayout: LAYOUT_OPTIONS.fiveColOver1
        }),
        service: this.dataSvc,
        mode: 'list',
        beforeEdit: this.beforeEdit.bind(this),
        readonly: true,
    });

    constructor() {
        const currentUserSvc = inject(CurrentUserService);

        super();
        const roleSvc = this.roleSvc;

        currentUserSvc.getCurrentUser().pipe(takeUntilDestroyed()).subscribe(user => {
            console.log(user);
            //if (user.inAgency) {
            //    this.config.fieldSet.formLayout = LAYOUT_OPTIONS.threeColOver1;
            //} else {
            //    this.config.fieldSet.formLayout = LAYOUT_OPTIONS.fiveCol;
            //}
        })
        roleSvc.get(true).subscribe(roles => {
            this.roles = roles as Role[];
            this.rolePicklistField.setPicklistItems(roles);
        });
    }

    beforeEdit(o: TeamUser) {
        const role = this.roles.find(r => r.id === o.roleId);
        if (role) {
            if (role.unitLinked) {
                this.rolePicklistField.setPicklistItems(this.roles);
                this.rolePicklistField.setReadonly();
            } else {
                this.rolePicklistField.setPicklistItems(this.roles.filter(ur => !ur.unitLinked));
                this.rolePicklistField.setReadonly(false);
            }
        } else {
            console.error('Role not found ', { o, role });
        }
        console.log({ o, f: this.auditGrid });
        return o;
    }
}
